import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Store scroll positions globally to persist across component remounts
const scrollPositions = new Map<string, number>();

/**
 * Hook to restore scroll position on browser back/forward navigation
 * and scroll to top on new route navigation
 */
export function useScrollRestoration(scrollToTop: boolean = true) {
	const location = useLocation();
	const isInitialMount = useRef(true);

	useEffect(() => {
		const currentPath = location.pathname;

		// On initial mount, check if we're navigating back/forward
		if (isInitialMount.current) {
			isInitialMount.current = false;
			
			// Check if browser has a saved scroll position (back/forward navigation)
			const savedPosition = scrollPositions.get(currentPath);
			if (savedPosition !== undefined && savedPosition > 0) {
				// Small delay to ensure DOM is ready
				setTimeout(() => {
					window.scrollTo({
						top: savedPosition,
						behavior: 'auto',
					});
				}, 0);
				return;
			}
		}

		// Scroll to top for new routes
		if (scrollToTop) {
			window.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
		}

		// Save scroll position before leaving
		const handleScroll = () => {
			scrollPositions.set(currentPath, window.scrollY);
		};

		// Throttle scroll events
		let ticking = false;
		const throttledScroll = () => {
			if (!ticking) {
				window.requestAnimationFrame(() => {
					handleScroll();
					ticking = false;
				});
				ticking = true;
			}
		};

		window.addEventListener('scroll', throttledScroll, { passive: true });

		return () => {
			window.removeEventListener('scroll', throttledScroll);
			// Save final scroll position
			scrollPositions.set(currentPath, window.scrollY);
		};
	}, [location.pathname, scrollToTop]);
}
